import * as React from 'react'

import { CheckIcon } from 'lucide-react'
import { classList } from 'react-classlist-helper'

import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import { useLocationSubtitle } from '~/client/src/shared/hooks/useLocationSubtitle'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

import { useResourcePickerContext } from '../ResourcePickerContext'

interface IProps {
  record: IResourceRecord
  subtitleKey?: string
}

const PickerItem: React.FC<IProps> = ({ record, subtitleKey }) => {
  const {
    selectedIds,
    mode,
    fieldProjector,
    pickItem,
    isItemDisabled,
    getRecordTypeId,
  } = useResourcePickerContext()

  const isSelected = selectedIds.has(record.id)
  const isDisabled = !isSelected && !!isItemDisabled?.(record.id)

  const handleClick = React.useCallback(() => {
    if (isDisabled) return
    pickItem(record.id)
  }, [isDisabled, pickItem, record.id])

  const subtitleValue = subtitleKey
    ? fieldProjector.resolve(record, subtitleKey)
    : null
  const resolvedSubtitle =
    subtitleValue && !subtitleValue.isEmpty ? subtitleValue.displayText : null

  // A location row carries its LBS beside its code, matching the mobile list. A no-op for
  // every other resource type.
  const appendLocationLbs = useLocationSubtitle(getRecordTypeId(record))
  const subtitle = appendLocationLbs(record, resolvedSubtitle)

  return (
    <div
      role="option"
      aria-selected={isSelected}
      aria-disabled={isDisabled || undefined}
      tabIndex={isDisabled ? undefined : 0}
      onClick={handleClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      className={classList({
        'tw:flex tw:items-start tw:gap-2 tw:py-2 tw:pl-1.5 tw:pr-2 tw:rounded-md tw:outline-none tw:transition-colors': true,
        'tw:cursor-pointer tw:hover:bg-accent-soft tw:focus-visible:bg-accent-soft tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-ring':
          !isDisabled,
        'tw:cursor-not-allowed tw:opacity-50': isDisabled,
        'tw:bg-accent-soft': isSelected,
      })}
    >
      {mode === 'multi' && (
        <span className="tw:flex tw:h-5 tw:shrink-0 tw:items-center">
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            displayOnly={true}
            aria-hidden={true}
            tabIndex={-1}
          />
        </span>
      )}

      <div className="tw:flex tw:flex-col tw:min-w-0 tw:flex-1">
        <span className="tw:text-app-dense-medium tw:text-popover-foreground tw:truncate">
          {record.name}
        </span>
        {subtitle && (
          <span className="tw:text-app-micro tw:text-muted-foreground tw:truncate">
            {subtitle}
          </span>
        )}
      </div>
      {mode === 'single' && (
        <span className="tw:flex tw:h-5 tw:shrink-0 tw:items-center">
          <CheckIcon
            aria-hidden="true"
            className={classList({
              'tw:size-4': true,
              'tw:invisible': !isSelected,
            })}
          />
        </span>
      )}
    </div>
  )
}

export default React.memo(PickerItem)
